import axiosClient from './axiosClient';
import { PermissionCode } from '../auth/permissionCatalog';

/**
 * Typed axios wrapper for `/api/admin/rbac-v2/{permissions,roles,users/:id/role}`
 * (architecture R1; verified against `server/src/routes/admin/rbacV2/{permissions,roles,userAssignments}.ts`).
 * Real backend counterpart of the reference branch's in-memory
 * `rolesModelApi.ts` mock — function names are kept close to that seam so
 * the adopted Roles-tab components swap cleanly, but the shapes follow the
 * REAL backend contract (snake_case, `role_code` as the identity — R3)
 * rather than the mock's `id`/camelCase.
 *
 * Mounted under the super-admin-only `/api/admin` stack; every mutation here
 * is env-local operational RBAC data (mirrors `/api/admin/users` today), so
 * callers use `WriteButton`/`WriteIconButton` with `ignoreEnvReadOnly`.
 */

/** GET /permissions row (config.app_permission columns, verbatim). */
export interface PermissionDef {
  permission_code: PermissionCode;
  label: string;
  description: string;
}

export interface RbacRole {
  role_code: string;
  role_name: string;
  description: string | null;
  permissions: PermissionCode[];
  is_system: boolean;
  /** Present on list rows so the Roles tab avoids an N+1 count fetch. */
  assigned_user_count?: number;
}

export interface RoleInput {
  role_name: string;
  description?: string | null;
  permissions: PermissionCode[];
}

export interface DeleteRoleResult {
  deleted: true;
}

export interface UserRoleAssignment {
  role_code: string;
}

const base = '/api/admin/rbac-v2';

/**
 * Derives a `role_code` slug from the role-name field the create dialog
 * actually collects (the adopted editor has no code input — matching the
 * reference UX — but the backend's `POST /roles` requires one, validated
 * against `^[a-z0-9][a-z0-9_-]{0,63}$`). A collision (`role_code_exists`/
 * `role_name_exists`) surfaces as a normal create-role error.
 */
export const slugifyRoleCode = (roleName: string): string =>
  roleName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '') || 'role';

export const rolesV2Api = {
  listPermissions: async (): Promise<PermissionDef[]> => {
    const { data } = await axiosClient.get<PermissionDef[]>(`${base}/permissions`);
    return data;
  },

  listRoles: async (): Promise<RbacRole[]> => {
    const { data } = await axiosClient.get<RbacRole[]>(`${base}/roles`);
    return data;
  },

  createRole: async (input: RoleInput): Promise<RbacRole> => {
    const { data } = await axiosClient.post<RbacRole>(`${base}/roles`, {
      role_code: slugifyRoleCode(input.role_name),
      ...input,
    });
    return data;
  },

  updateRole: async (roleCode: string, input: RoleInput): Promise<RbacRole> => {
    const { data } = await axiosClient.put<RbacRole>(
      `${base}/roles/${encodeURIComponent(roleCode)}`,
      input,
    );
    return data;
  },

  /** Live re-check ahead of a delete confirm — folds onto the list rows
   * (`assigned_user_count`) for everyday display; this is for the moment
   * right before a destructive action, when a stale count matters. */
  getAssignedUserCount: async (roleCode: string): Promise<number> => {
    const { data } = await axiosClient.get<{ count: number }>(
      `${base}/roles/${encodeURIComponent(roleCode)}/assignment-count`,
    );
    return data.count;
  },

  /** BLOCKED while any user holds the role (decisions §19) — the server
   * 409s with `{error: 'has_grants', count}` rather than returning a soft
   * `{deleted: false}`; callers handle that in the mutation's `onError`. */
  deleteRole: async (roleCode: string): Promise<DeleteRoleResult> => {
    await axiosClient.delete(`${base}/roles/${encodeURIComponent(roleCode)}`);
    return { deleted: true };
  },

  getUserRole: async (userId: number): Promise<UserRoleAssignment> => {
    const { data } = await axiosClient.get<UserRoleAssignment>(`${base}/users/${userId}/role`);
    return data;
  },

  setUserRole: async (userId: number, roleCode: string): Promise<UserRoleAssignment> => {
    const { data } = await axiosClient.put<UserRoleAssignment>(`${base}/users/${userId}/role`, {
      role_code: roleCode,
    });
    return data;
  },
};
